import { Howl } from 'howler';
import { getCharacter, type CharacterId } from '../game/characters';
import { loadSettings, type GameSettings } from '../game/settings';
import { pickOne, type RandomSource } from '../util/random';

export type VoiceAction = 'hover' | 'select';

/** Anything that can play a clip. Lets tests swap Howler out entirely. */
export interface Playable {
  play(): void;
  stop(): void;
  volume(level: number): void;
  /** Reads the playback position in seconds. Howler's own getter signature. */
  seek(): number;
  /** Playback rate, which also pitches the clip. 1 is unaltered. */
  rate(value: number): void;
  /** Whether the clip is audible right now. Howler's own getter. */
  playing(): boolean;
}

export type SoundFactory = (src: string, loop: boolean) => Playable;

const defaultFactory: SoundFactory = (src, loop) =>
  new Howl({ src: [src], loop }) as unknown as Playable;

const VOICE_TAKES = [1, 2, 3] as const;

/**
 * Owns every sound in the game and enforces the three audio toggles from the
 * settings screen. Clips are constructed on first use, so a muted category is
 * never even fetched.
 */
export class AudioManager {
  private readonly cache = new Map<string, Playable>();
  /**
   * The track the current screen wants, kept even while music is switched off
   * so that switching it back on knows what to resume.
   */
  private wantedTrack: { track: string; volume: number } | null = null;

  constructor(
    private settings: GameSettings = loadSettings(),
    private readonly factory: SoundFactory = defaultFactory,
    private readonly random: RandomSource = Math.random,
  ) {}

  applySettings(settings: GameSettings): void {
    const musicWasOn = this.settings.music;
    this.settings = settings;
    if (!settings.music) {
      this.silenceMusic();
    } else if (!musicWasOn && this.wantedTrack) {
      this.ensureMusic(this.wantedTrack.track, this.wantedTrack.volume);
    }
  }

  getSettings(): GameSettings {
    return this.settings;
  }

  /**
   * Plays a UI or impact sound.
   *
   * `rate` pitches the clip, which lets the two menu sounds stand in for the
   * whole game: a click at 0.45 is a rock impact, and a hover at 1.9 is the
   * ping for shaving past one. Cheaper than shipping more audio.
   */
  playEffect(name: 'mouse_over' | 'mouse_click', rate = 1): void {
    if (!this.settings.soundEffects) {
      return;
    }
    const sound = this.sound(`assets/sounds/${name}.wav`);
    sound.rate(rate);
    sound.play();
  }

  /**
   * Plays a random take so repeated hovers do not sound identical.
   * No-ops for racers whose voice pack has not been recorded yet.
   */
  playVoiceLine(character: CharacterId, action: VoiceAction): void {
    if (!this.settings.playerVoices || !getCharacter(character).hasVoice) {
      return;
    }
    const take = pickOne(VOICE_TAKES, this.random);
    this.sound(`assets/voice/${character}/player_select/${action}_${take}.wav`).play();
  }

  playMusic(track: string, volume = 0.2): void {
    this.wantedTrack = { track, volume };
    if (!this.settings.music) {
      return;
    }
    const music = this.sound(`assets/music/${track}`, true);
    music.volume(volume);
    music.play();
  }

  /**
   * Makes sure `track` is the one playing, without restarting it.
   *
   * Every menu screen calls this on entry, so the menu theme survives hopping
   * between them and comes back after a race has silenced it. Calling Howler's
   * `play()` on a track that is already playing would layer a second copy over
   * the first, which is why this checks before playing.
   */
  ensureMusic(track: string, volume = 0.2): void {
    this.wantedTrack = { track, volume };
    if (!this.settings.music || this.playingTrack() === track) {
      return;
    }
    this.silenceMusic();
    this.playMusic(track, volume);
  }

  /** The music track audible right now, or `null` for silence. */
  playingTrack(): string | null {
    for (const [key, sound] of this.cache) {
      if (key.startsWith('assets/music/') && sound.playing()) {
        return key.slice('assets/music/'.length).replace(/#loop$/, '');
      }
    }
    return null;
  }

  /**
   * How far the given track has played, in milliseconds, or `null` when there
   * is no position to read — music switched off, or the track never started.
   *
   * The intro paces its cues off this rather than off the frame clock, so that
   * the logo lands on the hit and the menu opens on the drop. See
   * docs/intro-sequence.md.
   */
  musicPositionMs(track: string): number | null {
    const sound = this.cache.get(this.key(`assets/music/${track}`, true));
    if (!sound) {
      return null;
    }
    const seconds = sound.seek();
    return typeof seconds === 'number' && Number.isFinite(seconds)
      ? seconds * 1000
      : null;
  }

  /** Stops all music and forgets what was wanted, as a race does. */
  stopMusic(): void {
    this.wantedTrack = null;
    this.silenceMusic();
  }

  private silenceMusic(): void {
    for (const [key, sound] of this.cache) {
      if (key.startsWith('assets/music/')) {
        sound.stop();
      }
    }
  }

  private sound(src: string, loop = false): Playable {
    const key = this.key(src, loop);
    let sound = this.cache.get(key);
    if (!sound) {
      sound = this.factory(src, loop);
      this.cache.set(key, sound);
    }
    return sound;
  }

  private key(src: string, loop: boolean): string {
    return loop ? `${src}#loop` : src;
  }
}
